import { Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import investmentTypeArticleLang from '../../features/fund-type/langs/investmentTypeArticleLang';
import CCapitalKpi from '../../features/fund-type/components/synthesis/private_credit/CCapitalKpi';
import CGraphPortfolioNav from '../../features/fund-type/components/synthesis/private_credit/CGraphPortfolioNav';
import CPerformanceKpi from '../../features/fund-type/components/synthesis/private_credit/CPerformanceKpi';
import CValueKpi from '../../features/fund-type/components/synthesis/private_credit/CValueKpi';
import _ from 'lodash';
import IFundTypeStatsNavSFDR from '../../interfaces/expertise/IFundTypeStatsNavSFDR';
import IFundTypeStatsNavDomain from '../../interfaces/expertise/IFundTypeStatsNavDomain';
import IFund from '../../interfaces/fund/IFund';
import IInvestKpis from '../../interfaces/IInvestKpis';
import langInvestWordLang from '../../langs/langInvestWordLang';
import IFundTypeStatsNavVintage from '../../interfaces/IFundTypeStatsNavVintage';
import { TInvestFundCurrency } from '../../types/fund/TInvestFundCurrency';
import { langLookup } from '../../../../app/utils/langLookup';

type VInvestExpertiseSynthesisPrivateCreditProps = {
	allFunds: IFund[];
	kpisByFund: {
		fund: IFund;
		kpis: IInvestKpis;
	}[];
	kpis: IInvestKpis;
	currency: TInvestFundCurrency;
};

const VInvestExpertiseSynthesisPrivateCredit = (props: VInvestExpertiseSynthesisPrivateCreditProps) => {
	const { allFunds, kpisByFund, kpis, currency } = props;
	const { t } = useTranslation();

	const [hasNavNC, setHasNavNC] = useState<boolean>(false);
	const [portfolioNavVintages, setPortfolioNavVintages] = useState<IFundTypeStatsNavVintage[]>([]);
	const [portfolioNavSfdrs, setPortfolioNavSfdrs] = useState<IFundTypeStatsNavSFDR[]>([]);
	const [portfolioNavDomains, setPortfolioNavDomains] = useState<IFundTypeStatsNavDomain[]>([]);

	useEffect(() => {
		if (allFunds && kpisByFund) {
			// Group by SFRD
			const portfolioNavSfdrsTmp = [] as IFundTypeStatsNavSFDR[];
			const groupSFRDs = _.groupBy(allFunds, 'sfdr');
			Object.keys(groupSFRDs).forEach((key) => {
				const groupSFRD = groupSFRDs[key];
				const itemTmp = {
					name:
						key && langLookup(investmentTypeArticleLang, key)
							? t(langLookup(investmentTypeArticleLang, key)?.tag ?? '')
							: t(langInvestWordLang.na.tag),
					kpisByFund: kpisByFund
						.filter((x) => groupSFRD.map((f) => f.id).includes(x.fund.id) && x.kpis.netAssetValue !== 0)
						.map((x) => ({
							...x,
							name: allFunds.find((f) => f.id === x.fund.id)?.name,
						})),
				} as IFundTypeStatsNavSFDR;

				portfolioNavSfdrsTmp.push(itemTmp);
			});
			setPortfolioNavSfdrs(portfolioNavSfdrsTmp.filter((x) => x.kpisByFund.length > 0));

			// Group by Domain
			const portfolioNavDomainsTmp = [] as IFundTypeStatsNavDomain[];
			const groupDomains = _.groupBy(allFunds, 'domainTag');
			Object.keys(groupDomains).forEach((key) => {
				const groupDomain = groupDomains[key];
				const itemTmp = {
					tag: key,
					kpisByFund: kpisByFund
						.filter((x) => groupDomain.map((f) => f.id).includes(x.fund.id) && x.kpis.netAssetValue !== 0)
						.map((x) => ({
							...x,
							name: allFunds.find((f) => f.id === x.fund.id)?.name,
						})),
				} as IFundTypeStatsNavDomain;

				portfolioNavDomainsTmp.push(itemTmp);
			});
			setPortfolioNavDomains(portfolioNavDomainsTmp.filter((x) => x.kpisByFund.length > 0));

			// Group by Vintage
			const portfolioNavVintagesTmp = [] as IFundTypeStatsNavVintage[];
			const groupVintages = _.groupBy(allFunds, 'vintage');
			Object.keys(groupVintages).forEach((key) => {
				const groupVintage = groupVintages[key];
				const itemTmp = {
					year: Number.parseInt(key),
					kpisByFund: kpisByFund
						.filter((x) => groupVintage.map((f) => f.id).includes(x.fund.id) && x.kpis.netAssetValue !== 0)
						.map((x) => ({
							...x,
							name: allFunds.find((f) => f.id === x.fund.id)?.name,
						})),
				} as IFundTypeStatsNavVintage;

				portfolioNavVintagesTmp.push(itemTmp);
			});
			setPortfolioNavVintages(portfolioNavVintagesTmp.filter((x) => x.kpisByFund.length > 0));
		}
	}, [allFunds, kpisByFund, t]);

	useEffect(() => {
		if (kpisByFund) {
			const hasNavNCTmp = kpisByFund.filter((x) => x.kpis.netAssetValue === null).length > 0;
			setHasNavNC(hasNavNCTmp);
		}
	}, [kpisByFund]);

	return (
		<>
			<Grid container spacing={3} alignItems="stretch">
				<Grid item sm={6} xs={12}>
					<CGraphPortfolioNav
						displayNavNc={hasNavNC}
						allFunds={allFunds}
						netAssetValue={kpisByFund ? _.sumBy(kpisByFund, 'kpis.netAssetValue') : 0}
						portfolioNavVintages={portfolioNavVintages}
						portfolioNavDomains={portfolioNavDomains}
						portfolioNavSfdrs={portfolioNavSfdrs}
						currency={currency}
					/>
				</Grid>
				<Grid item container sm={6} spacing={3} alignItems="stretch">
					<Grid item sm={12} xs={12}>
						<CCapitalKpi kpis={kpis} currency={currency} />
					</Grid>
					<Grid item sm={6} xs={12}>
						<CValueKpi kpis={kpis} currency={currency} />
					</Grid>
					<Grid item sm={6} xs={12}>
						<CPerformanceKpi kpis={kpis} />
					</Grid>
				</Grid>
			</Grid>
		</>
	);
};

export default VInvestExpertiseSynthesisPrivateCredit;
